/**
 * User schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, CurrencyCodeSchema, NestedPaginationParamsSchema, PublicationStateSchema, ReferrerTypeSchema, SortParamsSchema, StrapiIdSchema, } from "./common.schemas";
import { LazyBillingDataRelation, LazyChargeRelation, LazyCompanyRelation, LazyDiscountRelation, LazyExchangeAccountRelation, LazyPaymentMethodRelation, LazyPaymentRelation, LazyPayoutRelation, LazyReferralCodeRelation, LazyReferralRelation, LazyRefundRelation, LazyStrategyRelation, LazySubscriptionRelation, LazySubscriptionTierRelation, LazyUserRoleRelation, UserRelationSchema, } from "./relations";
// ============================================================================
// Enums
// ============================================================================
/**
 * Tax profile type schema
 */
export const TaxProfileTypeSchema = z.enum([
    "SPANISH_COMPANY",
    "SPANISH_AUTONOMO_EMPRESARIAL",
    "SPANISH_AUTONOMO_PROFESIONAL",
    "SPANISH_PARTICULAR",
    "EU_BUSINESS",
    "EU_PRIVATE",
    "NON_EU_BUSINESS",
    "NON_EU_PRIVATE",
]);
/**
 * Tax ID type schema
 */
export const TaxIdTypeSchema = z.enum(["NIF", "NIE", "CIF", "EU_VAT", "PASSPORT", "OTHER"]);
/**
 * Activity type schema
 */
export const ActivityTypeSchema = z.enum(["EMPRESARIAL", "PROFESIONAL", "PARTICULAR"]);
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * User update input schema
 * Matches: apps/api/src/extensions/users-permissions/controllers/user/updateMe.ts
 * For updateMe: ["name", "country", "language", "mainCurrency", "partnerInfo", "quizResult", "taxProfile"]
 * Admin updates can set additional fields
 */
export const UserUpdateInputSchema = z
    .object({
    // Fields allowed in updateMe
    name: z.string().optional(),
    country: z.string().optional(),
    language: z.string().optional(),
    mainCurrency: CurrencyCodeSchema.optional(),
    partnerInfo: z.record(z.unknown()).optional(),
    quizResult: z.record(z.unknown()).optional(),
    taxProfile: z.record(z.unknown()).optional(),
    // Admin-only fields
    email: z.string().email("Invalid email address").optional(),
    blocked: z.boolean().optional(),
    confirmed: z.boolean().optional(),
    isVIP: z.boolean().optional(),
    isTest: z.boolean().optional(),
    isManualPayment: z.boolean().optional(),
    manualPaymentReason: z.string().optional(),
    subscription_tier: StrapiIdSchema.nullish(),
})
    .strict();
/**
 * User filters schema - typesafe filters for user queries
 * Created using createEntityFilterSchema to reuse field types from UserRelationSchema
 */
export const UserFiltersSchema = createEntityFilterSchema(UserRelationSchema, {
    scalars: ["email", "username", "country", "confirmed", "blocked", "isVIP", "isTest", "referrerType"],
    relations: ["referral", "subscription_tier", "role"],
});
/**
 * Users query params schema
 * Uses typesafe filters based on UserFiltersSchema
 */
export const UsersQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: UserFiltersSchema.optional(),
    publicationState: PublicationStateSchema.optional(),
})
    .strict();
/**
 * Disable VIP input schema
 */
export const DisableVipInputSchema = z
    .object({
    userId: StrapiIdSchema,
    billingPeriodStart: z.string().optional(),
    subscriptionTierId: StrapiIdSchema.optional(),
})
    .strict();
/**
 * Disable VIP response schema
 * Matches: apps/api/src/extensions/users-permissions/controllers/user/disableVIP.ts
 */
export const DisableVipResponseSchema = z.object({
    success: z.boolean(),
    user: z.object({
        id: StrapiIdSchema,
        name: z.string(),
        email: z.string(),
        isVIP: z.boolean(),
    }),
    subscriptionsUpdated: z.number(),
    subscriptionsCreated: z.number(),
    freePeriodPackagesCreated: z.number(),
    warnings: z.array(z.string()),
});
// ============================================================================
// Component Schemas
// ============================================================================
/**
 * Tax profile schema - matches misc.tax-profile component in Strapi
 */
export const TaxProfileSchema = z.object({
    // Profile classification
    profileType: TaxProfileTypeSchema.nullish(),
    // Tax identification
    taxIdType: TaxIdTypeSchema.nullish(),
    taxIdNumber: z.string().nullish(),
    taxIdVerified: z.boolean().nullish(),
    // Location
    country: z.string().nullish(),
    countryCode: z.string().nullish(),
    // Personal/Business details
    name: z.string().nullish(),
    fiscalAddress: z.string().nullish(),
    city: z.string().nullish(),
    postalCode: z.string().nullish(),
    // Activity info (Spanish autónomos)
    activityType: ActivityTypeSchema.nullish(),
    iaeCode: z.string().nullish(),
    iaeDescription: z.string().nullish(),
    altaDate: z.string().nullish(),
    // Tax rates
    vatRate: z.number().nullish(),
    irpfRate: z.number().nullish(),
    // Flags
    isReverseCharge: z.boolean().nullish(),
    isVatExempt: z.boolean().nullish(),
    needsInvoice: z.boolean().nullish(),
    // VIES verification (EU Business)
    viesVerified: z.boolean().nullish(),
    viesVerifiedAt: z.string().nullish(),
    viesName: z.string().nullish(),
    viesAddress: z.string().nullish(),
    // Declarations and agreements
    autofacturaAgreement: z.boolean().nullish(),
    autofacturaAgreementDate: z.string().nullish(),
    particularDeclaration: z.boolean().nullish(),
    particularDeclarationDate: z.string().nullish(),
    privateIndividualDeclaration: z.boolean().nullish(),
    privateIndividualDeclarationDate: z.string().nullish(),
    riskAcknowledged: z.boolean().nullish(),
    // Status
    taxProfileComplete: z.boolean().nullish(),
    taxProfileCompletedAt: z.string().nullish(),
    thresholdWarning: z.boolean().nullish(),
    thresholdBlocked: z.boolean().nullish(),
    thresholdBlockReason: z.string().nullish(),
});
/**
 * Partner info schema
 */
export const PartnerInfoSchema = z.object({
    referrerType: ReferrerTypeSchema.nullish(),
    commissionRate: z.number().nullish(),
    customTerms: z.string().nullish(),
});
/**
 * User consent schema
 */
export const UserConsentSchema = z.object({
    marketing: z.boolean(),
    analytics: z.boolean(),
    thirdParty: z.boolean(),
    updatedAt: z.string(),
});
/**
 * Tax profile migration status schema
 */
export const TaxProfileMigrationStatusSchema = z.object({
    migrationEnabled: z.boolean().nullish(),
    hasTaxProfile: z.boolean().nullish(),
    taxProfileComplete: z.boolean().nullish(),
    hasOldBillingData: z.boolean().nullish(),
    inGracePeriod: z.boolean().nullish(),
    daysRemaining: z.number().nullish(),
    notifiedAt: z.string().nullish(),
    deadline: z.string().nullish(),
    suggestedData: z
        .object({
        name: z.string().nullish(),
        fiscalAddress: z.string().nullish(),
        country: z.string().nullish(),
        taxIdNumber: z.string().nullish(),
        taxIdType: z.string().nullish(),
    })
        .nullish(),
    isReferrer: z.boolean().nullish(),
});
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Quiz result schema - captures user quiz responses
 */
export const QuizResultSchema = z.object({
    answers: z.union([z.record(z.string(), z.string()), z.array(z.unknown())]).nullish(),
    score: z.number().nullish(),
    completedAt: z.string().nullish(),
    recommendedStrategy: z.string().nullish(),
});
/**
 * User schema - extends UserRelationSchema with additional fields and relations
 */
export const UserSchema = UserRelationSchema.extend({
    // Additional auth fields not in relation schema
    provider: z.string().nullish(),
    // Partner info (component) - not in relation schema
    partnerInfo: PartnerInfoSchema.nullish(),
    // Quiz - not in relation schema
    quizResult: QuizResultSchema.nullish(),
    // Trustpilot - not in relation schema
    trustpilotInvitationSent: z.boolean().nullish(),
    // Tax profile (component) - not in relation schema
    taxProfile: TaxProfileSchema.nullish(),
    // pm_* fields (injected by payments-manager plugin) - not in relation schema
    pm_billingData: LazyBillingDataRelation.nullish(),
    pm_billingDataAsIssuer: LazyBillingDataRelation.nullish(),
    // Relations (populated)
    role: LazyUserRoleRelation.nullish(),
    exchange_accounts: z.array(LazyExchangeAccountRelation).nullish(),
    referral: LazyReferralRelation.nullish(), // oneToOne - user is the referee
    referrals: z.array(LazyReferralRelation).nullish(), // oneToMany - user is the referrer
    referrer_codes: z.array(LazyReferralCodeRelation).nullish(),
    companies: z.array(LazyCompanyRelation).nullish(),
    strategies: z.array(LazyStrategyRelation).nullish(),
    discounts: z.array(LazyDiscountRelation).nullish(),
    subscription_tier: LazySubscriptionTierRelation.nullish(),
    // pm_* relations (injected by payments-manager plugin)
    pm_charges: z.array(LazyChargeRelation).nullish(),
    pm_mainPaymentMethod: LazyPaymentMethodRelation.nullish(),
    pm_paymentMethods: z.array(LazyPaymentMethodRelation).nullish(),
    pm_payments: z.array(LazyPaymentRelation).nullish(),
    pm_payouts: z.array(LazyPayoutRelation).nullish(),
    pm_refunds: z.array(LazyRefundRelation).nullish(),
    pm_subscriptions: z.array(LazySubscriptionRelation).nullish(),
});
